import axios from "axios";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";
import { geoJSON } from "leaflet";
import { Geometry } from "geojson";
import { useSnackbar } from "notistack";
import {
  AdaptdationIndicator,
  ExampleStudyArea,
  MappedAdaptationIndicatorValue,
  MeasureImpact,
  ProgressStatus,
  Result,
  StudyArea,
  Uuid,
} from "types/types";

import { deserializeErrors } from "utils/ErrorUtils";
import { customGet, customPost, customPut } from "utils/AxiosUtils";
import { BackendMeasureImpact, BackendStudyArea } from "types/backend";
import { deserializeMeasure } from "./measureHooks";

export function useCreateStudyArea() {
  async function post(studyArea: Geometry) {
    const { data: uuid } = await customPost<Uuid>(`/api/studyareas`, {
      geometry_4326: studyArea,
    });
    return uuid;
  }
  return useMutation(["studyArea"], post, {
    onSuccess: () => {},
  });
}

export function useStudyArea(
  uuid: string,
  options?: Pick<UseQueryOptions, "enabled">
) {
  const { enqueueSnackbar } = useSnackbar();
  async function fetch(): Promise<StudyArea> {
    const { data } = await customGet<BackendStudyArea>(
      `/api/studyareas/${uuid}`
    );
    return deserializeStudyArea(data);
  }
  return useQuery(["studyareas", uuid], fetch, {
    onError: (err) => {
      const errors = deserializeErrors(err);
      errors.map((er) => {
        return enqueueSnackbar(er.message, {
          variant: "error",
          preventDuplicate: true,
        });
      });
    },
    cacheTime: Infinity,
    staleTime: Infinity,
    ...options,
  });
}

export function useUpdateStudyArea(uuid: string) {
  const queryClient = useQueryClient();
  async function update(studyArea: Geometry) {
    await customPut(`/api/studyareas/${uuid}`, {
      geometry_4326: studyArea,
    });
  }

  return useMutation(update, {
    onSuccess: () => {
      queryClient.invalidateQueries(["studyareas", uuid]);
    },
  });
}

function deserializeStudyArea(studyArea: BackendStudyArea): StudyArea {
  const { geometry_4326, buffer_4326, measureImpactList, ...restStudyArea } =
    studyArea;

  const deserializedMeasureImpacts: MeasureImpact[] = measureImpactList.map(
    (measureImpact: BackendMeasureImpact) => {
      const { measure, ...restMeasureImpact } = measureImpact;

      const deserializedMeasure = deserializeMeasure(measure);

      return {
        ...restMeasureImpact,
        measure: { ...deserializedMeasure },
      };
    }
  );

  return {
    ...restStudyArea,
    geometry_4326: geoJSON(geometry_4326),
    buffer_4326: geoJSON(buffer_4326),
    measureImpactList: deserializedMeasureImpacts,
  };
}

export function useStartCalculationStudyArea(uuid: string) {
  const queryClient = useQueryClient();

  async function post(uuid: string) {
    const { data } = await customPost<number>(
      `/api/studyareas/${uuid}/computation`,
      {} //Empty body, if no empty body is sent the request will be rejected by tomcat (403) - CORS Error Tomcat filter
    );
    return data;
  }
  return useMutation(["measureConfigurations", "post", uuid], post, {
    onSuccess: () => {
      queryClient.invalidateQueries(["computationResult", uuid]);
    },
  });
}

export function useComputationProgressStudyArea(
  uuid: string,
  progressId: number,
  options?: Pick<UseQueryOptions, "enabled">
) {
  async function fetch(): Promise<ProgressStatus> {
    const { data } = await axios.get<ProgressStatus>(
      `/api/studyareas/${uuid}/computation/progress/${progressId}`
    );
    return data;
  }
  return useQuery(["computationProgress", uuid, progressId], fetch, {
    refetchInterval: 1000,
    suspense: false,
    ...options,
  });
}

export function useStudyAreaResult(uuid: string) {
  async function fetch(): Promise<Result> {
    const { data } = await axios.get<Result>(`/api/studyareas/${uuid}/results`);
    return data;
  }
  return useQuery(["computationResult", uuid], fetch, {
    cacheTime: Infinity,
    staleTime: Infinity,
  });
}

export function useCloneProject() {
  async function post(uuid: string) {
    const { data } = await axios.post<Uuid>(
      `/api/studyareas/${uuid}/duplication`,
      {} //Empty body, if no empty body is sent the request will be rejected by tomcat (403) - CORS Error Tomcat filter
    );
    return data;
  }
  return useMutation(["studyareas", "duplicate"], post);
}

export function useCalculatedAdaptionIndVals(
  adaptationIndicatorValues: AdaptdationIndicator[]
): MappedAdaptationIndicatorValue[] {
  const currentAdapationValues = adaptationIndicatorValues.filter(
    (adaptation) => adaptation.scenario.presentYear
  );

  const futureAdaptationValues = adaptationIndicatorValues.filter(
    (adaptation) => !adaptation.scenario.presentYear
  );

  const mappedFutureAdaptionValues = futureAdaptationValues.map(
    (futureAdaptationValue) => {
      const currentAdaptationValue = currentAdapationValues.find(
        (currentAdaptationValue) =>
          currentAdaptationValue.key === futureAdaptationValue.key
      );
      return {
        value: futureAdaptationValue.value,
        description: futureAdaptationValue.description,
        difference: currentAdaptationValue
          ? futureAdaptationValue.value - currentAdaptationValue.value
          : 0,
        name: futureAdaptationValue.name,
        unit: futureAdaptationValue.unit,
      };
    }
  );
  return mappedFutureAdaptionValues;
}

export function useDemoStudyArea() {
  const { enqueueSnackbar } = useSnackbar();
  async function fetch(): Promise<StudyArea> {
    const { data } = await customGet<BackendStudyArea>(`/api/studyareas/demo`);
    return deserializeStudyArea(data);
  }
  return useQuery(["studyareas", "demo"], fetch, {
    onError: (err) => {
      const errors = deserializeErrors(err);
      errors.map((er) => {
        return enqueueSnackbar(er.message, {
          variant: "error",
          preventDuplicate: true,
        });
      });
    },
    cacheTime: Infinity,
    staleTime: Infinity,
  });
}

export function useExampleStudyAreas() {
  const { enqueueSnackbar } = useSnackbar();
  async function fetch() {
    const { data } = await customGet<ExampleStudyArea[]>(
      `/api/studyareas/examples`
    );
    return data;
  }
  return useQuery(["studyareas", "examples"], fetch, {
    onError: (err) => {
      const errors = deserializeErrors(err);
      errors.map((er) => {
        return enqueueSnackbar(er.message, {
          variant: "error",
          preventDuplicate: true,
        });
      });
    },
    cacheTime: Infinity,
    staleTime: Infinity,
  });
}
